import React from "react";
import {
  CashDrawerSession,
  DENOMINATION_LABELS,
  DenominationEntry,
} from "../../types/cashDrawer";
import {
  formatCurrency,
  formatDrawerDateTime,
  formatTransactionType,
} from "../../utils/cashDrawerUtils";
import styles from "./CashDrawerPrintReport.module.css";

interface Props {
  session: CashDrawerSession;
  className?: string;
  timezone: string;
}

export const CashDrawerPrintReport: React.FC<Props> = ({
  session,
  className,
  timezone,
}) => {
  const transactions = session.transactions ?? [];
  const metrics = session.metrics;
  const countedDenominations: DenominationEntry[] =
    session.counted_denominations ?? [];

  let running = Number(session.opening_balance) || 0;
  const rows = transactions.map((tx) => {
    const signed = Number(tx.direction) * Number(tx.amount);
    if (!tx.voided_at && tx.type !== "opening_balance") {
      running += signed;
    }
    return { tx, signed, running };
  });

  return (
    <div className={`${styles.root} ${className ?? ""}`}>
      <h1 className={styles.title}>Cash Drawer Report</h1>
      <p className={styles.subtitle}>
        {formatDrawerDateTime(session.opened_at, timezone)} →{" "}
        {formatDrawerDateTime(session.closed_at, timezone)}
        {" — "}Drawer {session.status}
      </p>

      <table className={styles.summaryTable}>
        <tbody>
          <tr>
            <th>Opening balance</th>
            <td>{formatCurrency(session.opening_balance)}</td>
          </tr>
          <tr>
            <th>Expected balance</th>
            <td>{formatCurrency(session.expected_balance)}</td>
          </tr>
          {session.counted_balance !== null &&
            session.counted_balance !== undefined && (
              <tr>
                <th>Counted balance</th>
                <td>{formatCurrency(session.counted_balance)}</td>
              </tr>
            )}
          {session.variance !== null && session.variance !== undefined && (
            <tr>
              <th>Variance</th>
              <td
                className={
                  Number(session.variance) < 0
                    ? styles.negative
                    : styles.positive
                }
              >
                {formatCurrency(session.variance, { showSign: true })}
              </td>
            </tr>
          )}
          {session.closing_note && (
            <tr>
              <th>Note</th>
              <td>{session.closing_note}</td>
            </tr>
          )}
        </tbody>
      </table>

      {metrics && (
        <>
          <div className={styles.sectionHeading}>Metrics</div>
          <table className={styles.summaryTable}>
            <tbody>
              <tr>
                <th>Cash sales</th>
                <td>{formatCurrency(metrics.cash_sales)}</td>
              </tr>
              <tr>
                <th>Check sales</th>
                <td>{formatCurrency(metrics.check_sales)}</td>
              </tr>
              <tr>
                <th>Refunds</th>
                {/* Refunds are money out — shown in accounting parentheses
                    ($123.45) via the contra option. */}
                <td>{formatCurrency(metrics.refunds, { contra: true })}</td>
              </tr>
              <tr>
                <th>Paid in</th>
                <td>{formatCurrency(metrics.paid_in)}</td>
              </tr>
              <tr>
                <th>Expenses</th>
                <td>{formatCurrency(metrics.expenses)}</td>
              </tr>
              <tr>
                <th>Deposits</th>
                <td>{formatCurrency(metrics.deposits)}</td>
              </tr>
              <tr>
                <th>Tips</th>
                <td>{formatCurrency(metrics.tips)}</td>
              </tr>
            </tbody>
          </table>
        </>
      )}

      <div className={styles.sectionHeading}>Transactions</div>
      {rows.length === 0 ? (
        <p>No transactions recorded.</p>
      ) : (
        <table className={styles.txTable}>
          <thead>
            <tr>
              <th style={{ width: "20%" }}>Time</th>
              <th style={{ width: "16%" }}>Type</th>
              <th style={{ width: "28%" }}>Description</th>
              <th style={{ width: "13%" }}>Staff</th>
              <th className={styles.alignRight} style={{ width: "11%" }}>
                Amount
              </th>
              <th className={styles.alignRight} style={{ width: "12%" }}>
                Running
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ tx, signed, running: runningBal }) => (
              <tr
                key={tx.id}
                className={tx.voided_at ? styles.voided : undefined}
              >
                <td>{formatDrawerDateTime(tx.created_dt, timezone)}</td>
                <td>{formatTransactionType(tx)}</td>
                <td>{tx.description || "—"}</td>
                <td>{tx.staff_id || "—"}</td>
                <td
                  className={`${styles.alignRight} ${
                    signed >= 0 ? styles.positive : styles.negative
                  }`}
                >
                  {formatCurrency(signed, { showSign: true })}
                </td>
                <td className={styles.alignRight}>
                  {formatCurrency(runningBal)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {countedDenominations.length > 0 && (
        <>
          <div className={styles.sectionHeading}>Counted denominations</div>
          <table className={styles.txTable}>
            <tbody>
              {countedDenominations.map((entry) => (
                <tr key={entry.denomination}>
                  <td style={{ width: "50%" }}>
                    {DENOMINATION_LABELS[entry.denomination] ??
                      entry.denomination}
                  </td>
                  <td className={styles.alignRight}>× {entry.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <p className={styles.footerNote}>
        Generated {new Date().toLocaleString()}
      </p>
    </div>
  );
};

export default CashDrawerPrintReport;
